import React, { useMemo } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { EmptyState } from '@/components/system/EmptyState';
import { BarChart3 } from 'lucide-react';
import type { IFContratoResumo } from '../types/financiamento.types';

interface Props {
  resumos: IFContratoResumo[];
}

const RISK_COLS = ['crítico', 'alto', 'moderado', 'baixo'] as const;
const RISK_LABELS: Record<string, string> = {
  'crítico': 'Crítico',
  'critico': 'Crítico',
  'alto': 'Alto',
  'moderado': 'Moderado',
  'baixo': 'Baixo',
};

function normalizeRisk(r: string | null): string {
  if (!r) return 'baixo';
  if (r === 'critico') return 'crítico';
  return r;
}

function cellBg(count: number, total: number): string {
  if (count === 0) return 'bg-muted/30';
  const pct = count / Math.max(1, total);
  if (pct > 0.3) return 'bg-destructive/40';
  if (pct > 0.15) return 'bg-destructive/25';
  if (pct > 0) return 'bg-destructive/10';
  return 'bg-muted/30';
}

function cellText(count: number, total: number): string {
  if (count === 0) return 'text-muted-foreground/30';
  const pct = count / Math.max(1, total);
  if (pct > 0.3) return 'text-destructive font-bold';
  if (pct > 0.15) return 'text-destructive/80 font-semibold';
  return 'text-foreground font-medium';
}

export const IFRiskHeatmap: React.FC<Props> = ({ resumos }) => {
  const { rows, total } = useMemo(() => {
    const byContratada = new Map<string, Map<string, IFContratoResumo[]>>();

    resumos.forEach(r => {
      const emp = r.contratada || 'Sem contratada';
      const risk = normalizeRisk(r.classificacao_risco);
      if (!byContratada.has(emp)) byContratada.set(emp, new Map());
      const riskMap = byContratada.get(emp)!;
      if (!riskMap.has(risk)) riskMap.set(risk, []);
      riskMap.get(risk)!.push(r);
    });

    // Sort contractors by total critical+alto descending
    const sorted = Array.from(byContratada.entries()).sort((a, b) => {
      const countA = (a[1].get('crítico')?.length || 0) + (a[1].get('alto')?.length || 0);
      const countB = (b[1].get('crítico')?.length || 0) + (b[1].get('alto')?.length || 0);
      return countB - countA;
    });

    return { rows: sorted, total: resumos.length };
  }, [resumos]);

  if (rows.length === 0) {
    return <EmptyState text="Nenhum contrato na carteira de monitoramento." icon={BarChart3} />;
  }

  return (
    <TooltipProvider>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider pb-2 pr-4 min-w-[140px]">
                Contratada
              </th>
              {RISK_COLS.map(col => (
                <th key={col} className="text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider pb-2 px-2 min-w-[70px]">
                  {RISK_LABELS[col]}
                </th>
              ))}
              <th className="text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider pb-2 px-2 min-w-[50px]">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([emp, riskMap]) => {
              const empTotal = Array.from(riskMap.values()).reduce((s, arr) => s + arr.length, 0);
              return (
                <tr key={emp} className="border-t border-border/20">
                  <td className="py-2 pr-4 text-xs font-medium truncate max-w-[200px]" title={emp}>
                    {emp}
                  </td>
                  {RISK_COLS.map(col => {
                    const contracts = riskMap.get(col) || [];
                    const count = contracts.length;
                    return (
                      <td key={col} className="py-2 px-2 text-center">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className={`inline-flex items-center justify-center h-7 w-10 rounded ${cellBg(count, total)} ${cellText(count, total)} text-xs cursor-default transition-colors`}>
                              {count}
                            </div>
                          </TooltipTrigger>
                          {count > 0 && (
                            <TooltipContent side="top" className="max-w-[240px]">
                              <p className="font-semibold text-xs mb-1">{emp} — {RISK_LABELS[col]}</p>
                              {contracts.slice(0, 5).map(c => (
                                <p key={c.contrato_id} className="text-[11px] text-muted-foreground truncate">
                                  • {c.contrato_nome}
                                </p>
                              ))}
                              {contracts.length > 5 && (
                                <p className="text-[10px] text-muted-foreground/60 mt-0.5">+{contracts.length - 5} mais</p>
                              )}
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </td>
                    );
                  })}
                  <td className="py-2 px-2 text-center text-xs font-semibold text-muted-foreground">
                    {empTotal}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
};
